// Lightweight mask helper centralized for TemplateDesigner and render components
// Exports:
//   - formatWithMask(inputVal, maskTemplate, promptChar)
//   - extractForMask(val)
//   - attachMaskHandlersToRoot(root)

import $ from 'jquery';

export function formatWithMask(inputVal, maskTemplate, promptChar = '_') {
  const val = (inputVal || '').toString();
  let out = '';
  let vpos = 0;

  const nextMatching = function (start, predicate) {
    for (let i = start; i < val.length; i++) {
      const ch = val.charAt(i);
      if (predicate(ch)) return { ch: ch, idx: i };
    }
    return null;
  };

  maskTemplate = maskTemplate || '';

  for (let i = 0; i < maskTemplate.length; i++) {
    const mch = maskTemplate.charAt(i);
    if (mch === '0') {
      const found = nextMatching(vpos, c => /[0-9]/.test(c));
      if (found) {
        out += found.ch;
        vpos = found.idx + 1;
      } else {
        out += promptChar;
      }
    } else if (mch === 'A') {
      const found = nextMatching(vpos, c => /[A-Za-z]/.test(c));
      if (found) {
        out += found.ch;
        vpos = found.idx + 1;
      } else {
        out += promptChar;
      }
    } else {
      // literal from mask
      out += mch;
      // If literal matches current input char, advance vpos to avoid duplicating
      if (vpos < val.length && val.charAt(vpos) === mch) vpos++;
    }
  }

  return out;
}

export function extractForMask(val) {
  // Return only user-entered characters relevant for masks (letters and digits)
  try {
    return ((val || '').toString()).replace(/[^0-9A-Za-z]/g, '');
  } catch (e) {
    return '';
  }
}

export function attachMaskHandlersToRoot(root) {
  try {
    const $root = $(root || document);

    // Attempts to discover mask and promptChar for a given input element by inspecting
    // element attributes and walking ancestors for a data-field JSON blob.
    const discoverMaskForInput = function ($input) {
      // direct attrs first
      let mask = $input.attr('data-mask') || $input.attr('mask') || $input.data('mask') || '';
      let prompt = $input.attr('data-promptchar') || $input.attr('promptChar') || $input.data('promptchar') || $input.data('promptChar') || '';

      if (!mask) {
        // scan attributes for anything that contains 'mask' (defensive)
        try {
          const el = $input.get(0);
          if (el && el.attributes) {
            for (let i = 0; i < el.attributes.length; i++) {
              const a = el.attributes[i];
              const name = (a.name || '').toLowerCase();
              if (!mask && name.indexOf('mask') !== -1) mask = a.value || mask;
              if (!prompt && name.indexOf('prompt') !== -1) prompt = a.value || prompt;
            }
          }
        } catch (e) {
          // ignore
        }
      }

      if ((!mask || mask === '') && $input && $input.length) {
        // walk ancestors up to root and look for a data-field attribute that may contain JSON with mask/promptChar
        try {
          $input.parentsUntil($root).each(function () {
            try {
              const $p = $(this);
              const dataField = $p.attr('data-field') || $p.data('field') || $p.attr('data-fb-field') || $p.data('fb-field');
              if (dataField) {
                try {
                  const parsed = (typeof dataField === 'string') ? JSON.parse(dataField) : dataField;
                  if (parsed) {
                    if (!mask && parsed.mask) mask = parsed.mask;
                    if (!prompt && parsed.promptChar) prompt = parsed.promptChar;
                  }
                } catch (e) {
                  // ignore parse errors
                }
              }
            } catch (e) { /* ignore per-ancestor */ }
            if (mask && mask !== '') return false; // break out if found
          });
        } catch (e) { /* ignore */ }
      }

      return { mask: mask || '', prompt: prompt || '' };
    };

    const countUserCharsBeforePos = function (val, pos) {
      let cnt = 0;
      for (let i = 0; i < Math.min(pos, val.length); i++) {
        const ch = val.charAt(i);
        if (/[0-9A-Za-z]/.test(ch)) cnt++;
      }
      return cnt;
    };

    const findCaretPosForUserChars = function (formatted, userCharCount, promptChar) {
      if (userCharCount <= 0) {
        for (let j = 0; j < formatted.length; j++) {
          const cj = formatted.charAt(j);
          if (/[0-9A-Za-z]/.test(cj) || cj === promptChar) return 0;
        }
        return 0;
      }

      let seen = 0;
      for (let i = 0; i < formatted.length; i++) {
        const ch = formatted.charAt(i);
        if (/[0-9A-Za-z]/.test(ch)) {
          seen++;
          if (seen >= userCharCount) {
            let pos = i + 1;
            while (pos < formatted.length) {
              const cand = formatted.charAt(pos);
              if (cand === promptChar) return pos;
              if (/[0-9A-Za-z]/.test(cand)) return pos + 1;
              pos++;
            }
            return formatted.length;
          }
        }
      }

      for (let k = 0; k < formatted.length; k++) {
        const ck = formatted.charAt(k);
        if (ck === promptChar) return k;
      }
      return formatted.length;
    };

    // Delegate to any input with class 'masked-input' OR inputs that carry mask metadata attributes
    $root.off('input.maskHelper', 'input.masked-input, input[data-mask], input[mask]');
    $root.on('input.maskHelper', 'input.masked-input, input[data-mask], input[mask]', function (e) {
      try {
        const inp = this;
        const $inp = $(inp);
        // attempt to discover mask/prompt from the input or its ancestors
        const discovered = (typeof discoverMaskForInput === 'function') ? discoverMaskForInput($inp) : { mask: '', prompt: '' };
        const mask = (discovered && discovered.mask) || $inp.attr('data-mask') || $inp.prop('mask') || $inp.attr('mask') || '';
        const promptChar = (discovered && discovered.prompt) || $inp.attr('data-promptchar') || $inp.prop('promptChar') || $inp.attr('promptChar') || '_';

        // If no mask is available for this input, do not override user typing — bail out.
        if (!mask || mask === '') return;

        const beforeVal = $inp.val() || '';
        const selStart = (typeof inp.selectionStart === 'number') ? inp.selectionStart : beforeVal.length;

        // Count user characters (alphanumeric) before caret based on the current displayed value
        const userCountBefore = countUserCharsBeforePos(beforeVal, selStart);

        // Strip any prompt characters or mask literals so we only keep user-entered chars for formatting
        const raw = extractForMask(beforeVal);
        const formatted = formatWithMask(raw, mask, promptChar);

        $inp.val(formatted);

        const newPos = findCaretPosForUserChars(formatted, userCountBefore, promptChar);
        try { inp.setSelectionRange(newPos, newPos); } catch (ex) { /* ignore */ }
      } catch (ex) {
        // ignore
      }
    });

    // Initial pass to propagate mask metadata and format existing fields
    // This covers both formBuilder control panels (.frmb) and any other inputs that may have mask attributes
    $root.find('.frmb .frmb-field, .frmb .form-field').each(function () {
      try {
        const $f = $(this);
        const $input = $f.find('input[type="text"]').first();
        if (!$input || !$input.length) return;

        const maskAttr = $f.attr('mask') || $f.data('mask') || $input.attr('mask') || $input.data('mask');
        const promptAttr = $f.attr('data-promptchar') || $f.data('promptchar') || $input.attr('data-promptchar') || $input.data('promptchar') || $input.attr('promptChar') || $input.data('promptChar');

        if (maskAttr) {
          $input.attr('data-mask', maskAttr);
          if (promptAttr) $input.attr('data-promptchar', promptAttr);
          $input.addClass('masked-input');
          const raw = (extractForMask($input.val()) || '').replace(/[^0-9A-Za-z]/g, '');
          $input.val(formatWithMask(raw, maskAttr, promptAttr || '_'));

          // On focus, place caret at first editable placeholder
          $input.off('focus.maskHelper').on('focus.maskHelper', function () {
            try {
              const cur = $(this).val();
              const firstPromptIdx = (cur || '').indexOf(promptAttr || '_');
              if (firstPromptIdx >= 0) {
                try { this.setSelectionRange(firstPromptIdx, firstPromptIdx); } catch (e) { }
              } else {
                try { this.setSelectionRange(0, 0); } catch (e) { }
              }
            } catch (e) { /* ignore */ }
          });
        }
      } catch (e) {
        // ignore per-field
      }
    });

    // Also format any standalone inputs outside the formBuilder area that already declare a mask
    $root.find('input[data-mask], input[mask]').each(function () {
      try {
        const $input = $(this);
        const maskAttr = $input.attr('data-mask') || $input.attr('mask') || $input.data('mask');
        const promptAttr = $input.attr('data-promptchar') || $input.attr('promptChar') || $input.data('promptchar') || $input.data('promptChar') || '_';
        if (maskAttr) {
          $input.addClass('masked-input');
          $input.attr('data-mask', maskAttr);
          $input.attr('data-promptchar', promptAttr);
          const raw = (extractForMask($input.val()) || '').replace(/[^0-9A-Za-z]/g, '');
          $input.val(formatWithMask(raw, maskAttr, promptAttr || '_'));

          $input.off('focus.maskHelper').on('focus.maskHelper', function () {
            try {
              const cur = $(this).val();
              const firstPromptIdx = (cur || '').indexOf(promptAttr || '_');
              if (firstPromptIdx >= 0) {
                try { this.setSelectionRange(firstPromptIdx, firstPromptIdx); } catch (e) { }
              } else {
                try { this.setSelectionRange(0, 0); } catch (e) { }
              }
            } catch (e) { /* ignore */ }
          });
        }
      } catch (e) { /* ignore per-field */ }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('maskHelper.attachMaskHandlersToRoot failed', e);
  }
}

